import { getLinkSessionId } from '@/graphql/authHeader';
import { execute } from '@/graphql/execute';
import { MapLineweightMode, MeQuery, PointCloudPointSizeType, ThemePreference, UserViewerSettings } from '@/graphql/graphql';
import { updateThemeMutation, updateViewerSettingsMutation } from '@/mutations/user.mutation';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

export interface iTokenUser {
  sessionId: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  sid: string;
  sub: string;
}

export const userAtom = atom<MeQuery['me'] | null>(null);

// Session storage for viewer settings when user is not set
const viewerSettingsStorageKey = '@actegon.gis.viewerSettings';
const storage = createJSONStorage<UserViewerSettings>(() => sessionStorage);

const getDefaultValue = (): UserViewerSettings => ({
  pointCloudPointSizeType: 'ADAPTIVE',
  pointCloudPointSize: 1.0,
  orthoPointSize: 2,
  pointCloudLoadingDistance: 500,
  pointCloudMinNodePixelSize: 100,
  showLayerSource: false,
  showLayerUnderType: false,
  showImagePOIs: true,
  showAllImagePOIs: false,
  imagePOIMaxDistance: 50,
  pointCloudPointBudget: 10_000_000,
  showPointCloudBoundingBoxes: false,
  showOrthoGrid: false,
  googleStreetViewPoiColor: null,
  backgroundColor: null,
  customBaseMaps: [],
  actionPosition: 'BOTTOM',
  mapLineweightMode: MapLineweightMode.Screen,
});

// Shared-link visitors get a "load everything" point-cloud profile: the stock
// near-only LOD looks tiled to someone seeing the project once. Mobile still
// caps the budget in Pointcloud.tsx (MOBILE_POINT_BUDGET).
// "Group layers by type" is the link's own choice (`ProjectLink.groupLayersByType`).
const getSharedLinkDefaultValue = (groupLayersByType: boolean): UserViewerSettings => ({
  ...getDefaultValue(),
  showLayerUnderType: groupLayersByType,
  pointCloudPointSizeType: PointCloudPointSizeType.Fixed,
  pointCloudPointSize: 2,
  pointCloudPointBudget: 25_000_000,
  pointCloudLoadingDistance: 0,
  pointCloudMinNodePixelSize: 1,
});

/**
 * The open share link's `groupLayersByType` (set by `SharedPage` once the
 * `SharedLink` query loads); `null` until then, read as off.
 */
export const sharedLinkGroupLayersByTypeAtom = atom<boolean | null>(null);

const storageViewerSettingsAtom = atomWithStorage<UserViewerSettings>(
  viewerSettingsStorageKey,
  getDefaultValue(),
  storage,
);

export const userViewerSettingsAtom = atom(
  (get): UserViewerSettings => {
    const user = get(userAtom);
    const isSharedLink = !!getLinkSessionId();
    const defaults = isSharedLink
      ? getSharedLinkDefaultValue(get(sharedLinkGroupLayersByTypeAtom) ?? false)
      : getDefaultValue();
    if (user?.viewerSettings && !isSharedLink) {
      const stored = get(storageViewerSettingsAtom);
      // Only spread sessionStorage values that were explicitly set (not defaults).
      // Without this, default values (e.g. customBaseMaps: []) from a fresh
      // sessionStorage would overwrite server-persisted values.
      const hasStoredSession = sessionStorage.getItem(viewerSettingsStorageKey) !== null;
      const merged = hasStoredSession
        ? { ...defaults, ...user.viewerSettings, ...stored }
        : { ...defaults, ...user.viewerSettings };
      return merged;
    }
    // Only an explicit change made in this tab overrides the defaults; the
    // atom's own initial value is the non-shared default profile.
    const stored = get(storageViewerSettingsAtom);
    const hasStoredSession = sessionStorage.getItem(viewerSettingsStorageKey) !== null;
    return {
      ...defaults,
      ...(hasStoredSession ? stored : {}),
    };
  },
  (get, set, newSettings: UserViewerSettings) => {
    const user = get(userAtom);

    set(storageViewerSettingsAtom, newSettings);

    // A share link must not rewrite the settings of whoever is signed in.
    if (user && !getLinkSessionId()) {
      set(userAtom, {
        ...user,
        viewerSettings: newSettings,
      });

      execute(updateViewerSettingsMutation, {
        input: {
          pointCloudPointSizeType: newSettings.pointCloudPointSizeType,
          pointCloudPointSize: newSettings.pointCloudPointSize,
          orthoPointSize: newSettings.orthoPointSize,
          pointCloudLoadingDistance: newSettings.pointCloudLoadingDistance,
          pointCloudMinNodePixelSize: newSettings.pointCloudMinNodePixelSize,
          showLayerSource: newSettings.showLayerSource,
          showLayerUnderType: newSettings.showLayerUnderType,
          showImagePOIs: newSettings.showImagePOIs,
          showAllImagePOIs: newSettings.showAllImagePOIs,
          imagePOIMaxDistance: newSettings.imagePOIMaxDistance,
          pointCloudPointBudget: newSettings.pointCloudPointBudget,
          showPointCloudBoundingBoxes: newSettings.showPointCloudBoundingBoxes,
          showOrthoGrid: newSettings.showOrthoGrid,
          googleStreetViewPoiColor: newSettings.googleStreetViewPoiColor,
          backgroundColor: newSettings.backgroundColor,
          customBaseMaps: newSettings.customBaseMaps,
          actionPosition: newSettings.actionPosition,
          mapLineweightMode: newSettings.mapLineweightMode,
        },
      });
    }
  },
);

export type eTheme = ThemePreference;

export const userThemeAtom = atom(
  (get): eTheme | null => {
    const user = get(userAtom);
    if (!user) return null;
    return user.theme;
  },
  (get, set, theme: eTheme) => {
    const user = get(userAtom);
    if (user) {
      set(userAtom, { ...user, theme });
      execute(updateThemeMutation, { theme });
    }
  },
);
